import { get } from 'lib/api'
import { CapsuleState, UserState } from 'lib/types/models'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { AppState } from 'store'
import { VerifyAccount } from 'components/VerifyAccount'
import Lottie from 'react-lottie'
import { CapsuleLocked } from './components/CapsuleLocked'
import { CapsuleOpen } from './components/CapsuleOpen'
import { WelcomeScreen } from './components/WelcomeScreen'
import { DownloadScreen } from './components/DownloadScreen'
import { GiftScreen } from './components/GiftScreen'
import Logo from '../../lib/images/logo-full.png'
import animationData from '../../animations/connecting-2.json'

type ScreenType = 'welcome' | 'gift' | 'download' | 'locked' | 'open'

const EVENT_CAPSULES = [
  '76365a22-7e88-44de-89f8-68bac77fd77b',
  '1e8f95b9-91df-4405-8067-9e4dd88438ca',
  '6b4e3b1d-d2c7-4fcc-b2df-79d9151f1cf2',
  '6401350d-22d0-43e7-a685-6125bc13564f',
  'bafbbd26-348c-4aeb-8a5d-d579b691f713',
  'a247afca-0891-4b7e-80cc-8e8786f4fc1b',
  'ecde9375-929c-48fb-82bb-17f839e94413',
  '55d8c1d5-b362-4886-8473-3476a8aabf41',
  'a13c1d78-8451-480c-933e-7212f12acd1a',
  '6a337da8-938d-4c88-864f-a8703c75a720',
  '40a50972-ab97-4bc0-81e1-7396fafaebc1',
  '4f029d60-b40a-4930-a62e-5c16cd4c7bb6',
  'c7439e21-cdf2-4250-9fb8-dea7ecb58319',
  'a1d8337f-72aa-4974-9111-ac38ad3784c3',
  '0d237786-521a-4d8d-a243-14cd599c1f8b',
  '2a3ec9c4-4111-47f4-81d3-fd44d0da6f77',
  '53118aa4-68db-492e-a14c-2e9ca5583294',
  '20fa3dc9-a7df-48b1-985c-a1bc4bde195f',
  'b32bc05b-e6c7-412c-b72f-89b6063f7e81',
  'd31f7ba6-a8db-40cc-acd7-0e1cf98bac85',
]

const REDIRECT_CAPSULES = {
  'fad0df2a-5d4b-4270-869e-7607c7f33eb4':
    '009eab48-90cf-4efc-a5a5-7ae0e8209255',

  '0da7a8a4-35cc-41a8-bf78-bd63fe2c3e58':
    '611943be-add3-4d9c-bad1-350dfa7df660',
}

const TIME = 2000

const Capsule: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null)

  const [loading, setLoading] = useState(true)
  const [capsule, setCapsule] = useState<CapsuleState | null>(null)
  const [currentScreen, setCurrentScreen] = useState<ScreenType>('welcome')
  const [senderName, setSenderName] = useState<string>('')

  const params = useParams()
  const navigate = useNavigate()

  const user = useSelector<AppState, UserState>(({ user }) => user)

  const fetchCapsule = async () => {
    // Eğer UUID yoksa (root "/" route), direkt welcome screen göster
    if (!params.uuid) {
      setLoading(false)
      setCurrentScreen('welcome')
      return
    }

    try {
      const { data } = await get(`/capsules/get-capsule/${params.uuid}`)

      setCapsule(data)
      setLoading(false)

      // Basitleştirilmiş Flow Logic - Sadece locked durumuna bakıyoruz
      // 1. Locked değilse → WelcomeScreen → DownloadScreen
      if (!data.locked) {
        setCurrentScreen('welcome')
        return
      }

      // 2. Locked ise → GiftScreen (geri sayım) → DownloadScreen
      if (data.locked) {
        // Sender bilgisini al
        setSenderName(data.sender?.full_name || 'Gönderen')
        setCurrentScreen('gift')
        return
      }
    } catch {
      try {
        const { data } = await get(`/event/get-event/${params.uuid}`)
        window.location.href = `/event?code=${data.code}`
      } catch {
        // 404 yerine welcome screen'e yönlendir
        setLoading(false)
        setCurrentScreen('welcome')
      }
    }
  }

  useEffect(() => {
    setTimeout(() => {
      if (!ref.current) {
        return
      }

      ref.current.style.opacity = '1'
    }, 100)

    setTimeout(() => {
      if (!ref.current) {
        return
      }

      ref.current.style.opacity = '0'
    }, TIME + 200)

    // @ts-ignore
    if (REDIRECT_CAPSULES[params.uuid || '']) {
      // @ts-ignore
      window.location.href = `/${REDIRECT_CAPSULES[params.uuid || '']}`
    } else if (!EVENT_CAPSULES.includes(params.uuid || '')) {
      setTimeout(() => {
        fetchCapsule()
      }, TIME + 350)
    } else if (params.uuid === 'a1d8337f-72aa-4974-9111-ac38ad3784c3') {
      window.location.href = '/event?code=SDW9LX'
    } else {
      window.location.href = '/event'
    }
  }, [ref])

  if (loading) {
    return (
      <div
        className="flex flex-col items-center justify-center h-full opacity-0 transition-opacity duration-[250ms]"
        ref={ref}
      >
        <div className="w-full absolute top-[30px] left-0 flex justify-center">
          <img src={Logo} alt="logo" width={200} />
        </div>
        <Lottie
          options={{
            loop: true,
            autoplay: true,
            animationData,
          }}
          height={200}
          width={200}
        />

        <div className="text-[20px] leading-[1.6] text-dark-500 font-medium">
          Ürününle Bağlantı Sağlanıyor
        </div>
      </div>
    )
  }

  // Yeni Screen Rendering Logic
  const renderScreen = () => {
    const handleNext = () => {
      if (capsule && !capsule.receiver_id && params.uuid) {
        navigate(`/register?capsule=${params.uuid}`)
      } else {
        setCurrentScreen('download')
      }
    }

    switch (currentScreen) {
      case 'welcome':
        return <WelcomeScreen onNext={handleNext} />

      case 'gift':
        if (!capsule) return null
        return (
          <GiftScreen
            senderName={senderName}
            capsuleName={capsule.name || 'Zaman Kapsülü'}
            openingDate={capsule.opening_date || ''}
            onNext={handleNext}
          />
        )

      case 'download':
        return <DownloadScreen />

      case 'locked':
        if (!capsule) return null
        return (
          <CapsuleLocked
            capsule={capsule}
            onTimeIsOver={() => setCurrentScreen('open')}
          />
        )

      case 'open':
        if (!capsule) return null
        return <CapsuleOpen capsule={capsule} />

      default:
        return null
    }
  }

  if (user.authenticated && !user.is_verified && capsule) {
    return <VerifyAccount />
  }

  return <>{renderScreen()}</>
}

export default Capsule
